// ===== CONFIG =====
const SHOPIFY_DOMAIN = 'velvitnutrition.myshopify.com';
const STOREFRONT_TOKEN = '79dac18c849d1f08f0215da28f6fb802';
const STOREFRONT_URL = `https://${SHOPIFY_DOMAIN}/api/2024-01/graphql.json`;
const CART_ID_KEY = 'shopify_cart_id';

// ===== CART STATE =====
let cart = null;

// ===== SHOPIFY API =====
async function shopifyFetch(query, variables = {}) {
  const res = await fetch(STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors[0].message);
  return json.data;
}

const CART_FRAGMENT = `
  id checkoutUrl
  lines(first: 20) {
    edges { node {
      id quantity
      merchandise { ... on ProductVariant {
        id title
        product { title featuredImage { url altText } }
        priceV2 { amount currencyCode }
      }}
    }}
  }
  cost {
    totalAmount { amount currencyCode }
    subtotalAmount { amount currencyCode }
  }
`;

async function createCart() {
  const data = await shopifyFetch(`mutation { cartCreate { cart { ${CART_FRAGMENT} } } }`);
  return data.cartCreate.cart;
}

async function getCart(cartId) {
  const data = await shopifyFetch(`query($cartId:ID!){ cart(id:$cartId){ ${CART_FRAGMENT} } }`, { cartId });
  return data.cart;
}

async function addLineToCart(cartId, merchandiseId, quantity = 1) {
  const data = await shopifyFetch(
    `mutation($cartId:ID!,$lines:[CartLineInput!]!){ cartLinesAdd(cartId:$cartId,lines:$lines){ cart{ ${CART_FRAGMENT} } } }`,
    { cartId, lines: [{ merchandiseId, quantity }] }
  );
  return data.cartLinesAdd.cart;
}

async function removeLineFromCart(cartId, lineIds) {
  const data = await shopifyFetch(
    `mutation($cartId:ID!,$lineIds:[ID!]!){ cartLinesRemove(cartId:$cartId,lineIds:$lineIds){ cart{ ${CART_FRAGMENT} } } }`,
    { cartId, lineIds }
  );
  return data.cartLinesRemove.cart;
}

async function updateCartLineQty(cartId, lineId, quantity) {
  const data = await shopifyFetch(
    `mutation($cartId:ID!,$lines:[CartLineUpdateInput!]!){ cartLinesUpdate(cartId:$cartId,lines:$lines){ cart{ ${CART_FRAGMENT} } } }`,
    { cartId, lines: [{ id: lineId, quantity }] }
  );
  return data.cartLinesUpdate.cart;
}

async function fetchProducts() {
  const query = `{
    products(first: 20) {
      edges { node {
        id handle title description
        images(first:1){ edges{ node{ url altText } } }
        variants(first:1){ edges{ node{
          id availableForSale
          price{ amount currencyCode }
        }}}
      }}
    }
  }`;
  const data = await shopifyFetch(query);
  return data.products.edges.map(({ node }) => ({
    id: node.id,
    handle: node.handle,
    title: node.title,
    description: node.description,
    image: node.images.edges[0]?.node.url || '',
    imageAlt: node.images.edges[0]?.node.altText || node.title,
    price: node.variants.edges[0]?.node.price.amount || '0',
    currency: node.variants.edges[0]?.node.price.currencyCode || 'USD',
    availableForSale: true,
    variantId: node.variants.edges[0]?.node.id || '',
  }));
}

// ===== FORMAT =====
function formatPrice(amount, currency) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(parseFloat(amount));
}

// ===== CART UI =====
function openCart() {
  document.getElementById('cart-overlay').classList.add('open');
  document.getElementById('cart-panel').classList.add('open');
  document.body.style.overflow = 'hidden';
}
function closeCart() {
  document.getElementById('cart-overlay').classList.remove('open');
  document.getElementById('cart-panel').classList.remove('open');
  document.body.style.overflow = '';
}

function updateCartUI() {
  const lines = cart?.lines?.edges || [];
  const itemCount = lines.reduce((t, { node }) => t + node.quantity, 0);

  document.getElementById('cart-count').textContent = itemCount;
  document.getElementById('nav-cart-count').textContent = itemCount;
  document.getElementById('nav-cart-count').style.display = itemCount > 0 ? 'flex' : 'none';

  const body = document.getElementById('cart-body');
  const footer = document.getElementById('cart-footer');

  if (lines.length === 0) {
    body.innerHTML = `
      <div class="cart-empty">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
        <p>Your cart is empty</p>
        <p class="cart-empty-sub">Add some products to get started</p>
      </div>`;
    footer.style.display = 'none';
    return;
  }

  body.innerHTML = `<div class="cart-items">${lines.map(({ node: line }) => `
    <div class="cart-item" data-line-id="${line.id}">
      <div class="cart-item-top">
        ${line.merchandise.product.featuredImage ? `<img class="cart-item-img" src="${line.merchandise.product.featuredImage.url}" alt="${line.merchandise.product.featuredImage.altText || ''}" />` : ''}
        <div class="cart-item-details">
          <div class="cart-item-title">${line.merchandise.product.title}</div>
          ${line.merchandise.title !== 'Default Title' ? `<div class="cart-item-variant">${line.merchandise.title}</div>` : ''}
          <div class="cart-item-price">${formatPrice(line.merchandise.priceV2.amount, line.merchandise.priceV2.currencyCode)}</div>
        </div>
      </div>
      <div class="cart-item-bottom">
        <div class="qty-controls">
          <button class="qty-btn dec-btn" data-line-id="${line.id}" data-qty="${line.quantity}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
          <span class="qty-num">${line.quantity}</span>
          <button class="qty-btn inc-btn" data-line-id="${line.id}" data-qty="${line.quantity}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
        </div>
        <button class="remove-btn" data-line-id="${line.id}">Remove</button>
      </div>
    </div>`).join('')}</div>`;

  footer.style.display = 'flex';
  document.getElementById('cart-subtotal').textContent = formatPrice(cart.cost.subtotalAmount.amount, cart.cost.subtotalAmount.currencyCode);

  // Attach qty/remove events
  body.querySelectorAll('.inc-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const lineId = btn.dataset.lineId;
      const qty = parseInt(btn.dataset.qty);
      cart = await updateCartLineQty(cart.id, lineId, qty + 1);
      updateCartUI();
    });
  });
  body.querySelectorAll('.dec-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const lineId = btn.dataset.lineId;
      const qty = parseInt(btn.dataset.qty);
      if (qty <= 1) {
        cart = await removeLineFromCart(cart.id, [lineId]);
      } else {
        cart = await updateCartLineQty(cart.id, lineId, qty - 1);
      }
      updateCartUI();
    });
  });
  body.querySelectorAll('.remove-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      cart = await removeLineFromCart(cart.id, [btn.dataset.lineId]);
      updateCartUI();
    });
  });
}

// ===== PRODUCTS UI =====
async function loadProducts() {
  const grid = document.getElementById('products-grid');
  try {
    const products = await fetchProducts();
    if (products.length === 0) {
      grid.innerHTML = '<p class="products-loading">No products available</p>';
      return;
    }
    grid.innerHTML = products.map(p => `
      <div class="product-card">
        <a class="product-card-link" href="/products/${p.handle}">
          <div class="product-image">
            ${p.image
              ? `<img src="${p.image}" alt="${p.imageAlt}" loading="lazy" />`
              : `<div class="product-image-placeholder"><span>No image</span></div>`}
          </div>
          <div class="product-info">
            <h3 class="product-title">${p.title}</h3>
            <p class="product-desc">${p.description || 'Premium quality supplement'}</p>
            <div class="product-bottom">
              <span class="product-price">${formatPrice(p.price, p.currency)}</span>
              <button class="add-to-cart" data-variant-id="${p.variantId}" data-product-id="${p.id}">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 001.95-1.56L23 6H6"/></svg>
                Add to Cart
              </button>
            </div>
          </div>
        </a>
      </div>`).join('');

    grid.querySelectorAll('.add-to-cart').forEach(btn => {
      btn.addEventListener('click', async (ev) => {
        // Prevent card link navigation when clicking the button
        ev.preventDefault();
        ev.stopPropagation();
        const variantId = btn.dataset.variantId;
        btn.disabled = true;
        btn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg> Added!`;
        try {
          cart = await addLineToCart(cart.id, variantId);
          updateCartUI();
          openCart();
        } catch (e) { console.error(e); }
        setTimeout(() => {
          btn.disabled = false;
          btn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 001.95-1.56L23 6H6"/></svg> Add to Cart`;
        }, 1500);
      });
    });
  } catch (e) {
    grid.innerHTML = `<p style="color:#ef4444;text-align:center;padding:4rem">Error loading products</p>`;
    console.error(e);
  }
}

// ===== ANIMATION: BAR CHART =====
function initBarAnimation() {
  const bar = document.querySelector('.bar-fill.bright');
  if (!bar) return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) { bar.classList.add('animated'); observer.disconnect(); }
    });
  }, { threshold: 0.5 });
  observer.observe(bar);
}

// ===== NAV SCROLL =====
function initNavScroll() {
  // Nav is always visible — nothing to do. Just keeping for future use.
}

// ===== MOBILE MENU =====
function initMobileMenu() {
  const hamburger = document.getElementById('nav-hamburger');
  const overlay = document.getElementById('mobile-overlay');
  const menu = document.getElementById('mobile-menu');
  const closeBtn = document.getElementById('close-mobile-menu');
  const cartMobile = document.getElementById('open-cart-mobile');

  function openMenu() {
    overlay.classList.add('open');
    menu.classList.add('open');
    document.body.style.overflow = 'hidden';
  }
  function closeMenu() {
    overlay.classList.remove('open');
    menu.classList.remove('open');
    document.body.style.overflow = '';
  }

  hamburger?.addEventListener('click', openMenu);
  overlay?.addEventListener('click', closeMenu);
  closeBtn?.addEventListener('click', closeMenu);
  cartMobile?.addEventListener('click', () => { closeMenu(); openCart(); });
  menu?.querySelectorAll('a').forEach(a => a.addEventListener('click', closeMenu));
}

// ===== INIT =====
document.addEventListener('DOMContentLoaded', async () => {
  // Cart drawer
  document.getElementById('open-cart-btn')?.addEventListener('click', openCart);
  document.getElementById('close-cart')?.addEventListener('click', closeCart);
  document.getElementById('cart-overlay')?.addEventListener('click', closeCart);

  // Checkout
  document.getElementById('checkout-btn')?.addEventListener('click', () => {
    if (cart?.checkoutUrl) window.open(cart.checkoutUrl, '_blank');
  });

  // Init cart
  const savedId = localStorage.getItem(CART_ID_KEY);
  if (savedId) {
    try {
      cart = await getCart(savedId);
      if (!cart) throw new Error('null cart');
    } catch {
      localStorage.removeItem(CART_ID_KEY);
      cart = await createCart();
      localStorage.setItem(CART_ID_KEY, cart.id);
    }
  } else {
    cart = await createCart();
    localStorage.setItem(CART_ID_KEY, cart.id);
  }
  updateCartUI();

  // Products (only runs if the homepage grid exists)
  if (document.getElementById('products-grid')) {
    loadProducts();
  }

  // Animations (only if bar chart is present)
  initBarAnimation();

  // Mobile menu
  initMobileMenu();

  // Fire a custom event so page-specific scripts (e.g. product page) know cart is ready
  window.dispatchEvent(new CustomEvent('velvit:cart-ready', { detail: { cart } }));
});

// ===== PUBLIC API (for product page, etc.) =====
window.VelvitShop = {
  get cart() { return cart; },
  set cart(v) { cart = v; },
  shopifyFetch,
  addLineToCart,
  updateCartLineQty,
  removeLineFromCart,
  createCart,
  getCart,
  formatPrice,
  openCart,
  closeCart,
  updateCartUI,
  CART_ID_KEY,
};
